// @ts-check
// File: app/src/utils/planNodeUtils.js
//
// Pure, dependency-free helpers for inspecting workout-plan tree nodes:
// classifying recovery vs work-like nodes, resolving the bordering "work
// leaf" through nested Groups, and deriving a normalised exercise key.
//
// Extracted so both `transitionInjector.js` (which injects transitions
// between adjacent different exercises) and `planNormaliser.js` (which demotes
// logistical inter-exercise rests to transitions) can share one canonical
// implementation without importing each other — keeping the module graph
// acyclic. No React imports; never mutates the input.

/** @typedef {import("../types/plan").PlanNode} PlanNode */

// A node is a recovery node if its type is Rest / SetRest / Transition.
export const isRecoveryType = (type) => {
  const t = String(type || "").toLowerCase();
  return t === "rest" || t === "setrest" || t === "transition";
};

// "Work-like" = anything that performs movement: a Group (container of work)
// or a Work/Warmup/Cooldown leaf. Recovery nodes are explicitly excluded.
/** @param {PlanNode} node */
export const isWorkLike = (node) => {
  if (!node || isRecoveryType(node.type)) return false;
  if (node.type === "Group") return true;
  const t = String(node.type || "").toLowerCase();
  return t === "work" || t === "warmup" || t === "cooldown";
};

// Container node types — the only types allowed to hold `children`
// (docs/schema.md). A container can legitimately be EMPTY (an editor-created
// Group renders as an expandable block with an "Add step" affordance, issue
// #44), so this is a statement about the TYPE, not about the node's contents.
// Everything else is a leaf: `workoutCompiler` descends into any node with
// children but only emits leaves, so children hung off a leaf type silently
// discard that leaf's own prescription.
export const CONTAINER_TYPES = ["Group", "Circuit"];

export const isContainerType = (type) => CONTAINER_TYPES.includes(type);

// Normalised identity for an exercise (label-first, exercise_name fallback).
/** @param {PlanNode} node */
export const getExerciseKey = (node) => {
  if (!node) return null;
  return String(node.label || node.exercise_name || "")
    .trim()
    .toLowerCase();
};

// DFS through children to find the first leaf (non-Group, non-recovery) node.
/** @param {PlanNode} node */
export const getFirstWorkLeaf = (node) => {
  if (!node) return null;
  if (isRecoveryType(node.type)) return null;
  if (node.type !== "Group") return node;
  if (!Array.isArray(node.children)) return null;
  for (const child of node.children) {
    const leaf = getFirstWorkLeaf(child);
    if (leaf) return leaf;
  }
  return null;
};

// DFS through children to find the last leaf (non-Group, non-recovery) node.
/** @param {PlanNode} node */
export const getLastWorkLeaf = (node) => {
  if (!node) return null;
  if (isRecoveryType(node.type)) return null;
  if (node.type !== "Group") return node;
  if (!Array.isArray(node.children)) return null;
  for (let i = node.children.length - 1; i >= 0; i--) {
    const leaf = getLastWorkLeaf(node.children[i]);
    if (leaf) return leaf;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------
//
// Three families of unit, and every consumer must agree on which is which:
//
//   • TIME    ("s" / "m") — the phase runs a countdown; `value` is a duration.
//   • COUNTED ("reps" / "cal") — the phase is a self-paced target; `value` is
//     the count to hit and the phase advances on the user's Next press, never
//     on a clock. Its `durationSec` is an ESTIMATE, used for projections only.
//   • REPEAT  ("rounds" / "sets" / "workout") — a Group's iteration count.
//
// These predicates exist because the counted family used to be spelled
// `unit === "reps"` at a dozen call sites, which made "cal" (calorie targets on
// ergo machines — "100 cal Assault Bike") impossible to add without each site
// silently degrading: no ring value, zero contribution to the session estimate,
// no entry in the phase log.

export const TIME_UNITS = ["s", "m"];
export const COUNTED_UNITS = ["reps", "cal"];
export const REPEAT_UNITS = ["rounds", "sets", "workout"];

const normaliseUnit = (unit) => String(unit || "").toLowerCase();

export const isTimedUnit = (unit) => TIME_UNITS.includes(normaliseUnit(unit));

// A self-paced target the user counts off themselves (reps or calories).
export const isCountedUnit = (unit) =>
  COUNTED_UNITS.includes(normaliseUnit(unit));

export const isRepUnit = (unit) => normaliseUnit(unit) === "reps";
export const isCalorieUnit = (unit) => normaliseUnit(unit) === "cal";

// A Group's `value` is an iteration count under these units (contrast a
// time-capped Group, where `value` is a duration cap — see docs/schema.md).
export const isRepeatUnit = (unit) =>
  REPEAT_UNITS.includes(normaliseUnit(unit));
